import {useEffect, useRef, useState} from 'react';
import {debounce} from 'lodash';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    getLastValidPreviewFormValues,
    setLastValidPreviewFormValues,
} from 'components/customer/Preview3DCommon/store/viewerSlice';

type DebouncedPreviewCommit = ((nextProduct: PreviewFormValues) => void) & {
    cancel(): void;
    flush(): void | undefined;
};

const cloneValues = <T>(v: T): T => {
    try {
        if (typeof structuredClone === 'function') {
            return structuredClone(v) as unknown as T;
        }
        return JSON.parse(JSON.stringify(v)) as unknown as T;
    } catch {
        return v;
    }
};

export const useErrorHandler = ({
    product,
    hasValidationErrors,
}: {
    product: PreviewFormValues;
    hasValidationErrors: boolean;
}) => {
    const dispatch = useAppDispatch();
    const lastValidPreviewFormValues = useAppSelector(
        getLastValidPreviewFormValues
    );

    const hasErrorRef = useRef(false);
    hasErrorRef.current = hasValidationErrors;

    const [providedValues, setProvidedValues] =
        useState<PreviewFormValues>(product);

    const debouncedCommitRef = useRef<DebouncedPreviewCommit | null>(null);

    useEffect(() => {
        if (!debouncedCommitRef.current) {
            debouncedCommitRef.current = debounce(
                (nextProduct: PreviewFormValues) => {
                    const snapshot = cloneValues(nextProduct);
                    dispatch(setLastValidPreviewFormValues(snapshot));
                    setProvidedValues(snapshot);
                },
                300,
                {trailing: true}
            );
        }

        return () => {
            // Ensure no late writes fire after unmount.
            debouncedCommitRef.current?.cancel();
        };
    }, []);

    useEffect(() => {
        // While there are errors, keep serving the last known-good snapshot.
        if (hasErrorRef.current) {
            debouncedCommitRef.current?.cancel();
            setProvidedValues(lastValidPreviewFormValues ?? product);
            return;
        }
        debouncedCommitRef.current?.({...product});
    }, [product, hasValidationErrors]);

    return {providedValues};
};
